import type { Dispatch, SetStateAction } from "react"
import React, { useState } from "react"
import type { PaymentMethod } from "@mallardbay/lib-react-components"
import type { PaymentMethod as StripePaymentMethod } from "@stripe/stripe-js"

import { type PaymentMethodState } from "~utils/use-payment-method-state"
import { isPaymentMethodAch } from "~utils/stripe-helpers"
import { mapStripePaymentMethodToPaymentMethod } from "~components/pages/account/payment-methods/payment-method-helpers"
import PaymentMethodSelectFormCore from "~components/pages/account/payment-methods/payment-method-select-form-core"
import AddPaymentMethodButton from "~components/pages/account/payment-methods/add-payment-method-button"
import COPY from "~config/copy-constants"

interface Props {
    readonly selectedId: string | null
    readonly onChange: (update: Partial<PaymentMethodState>) => void
    readonly isExternalPaymentMethod?: boolean
    readonly isPayWithCheckEnabled?: boolean
}

export default function GuestPaymentMethodSelectForm({
    selectedId,
    onChange,
    isExternalPaymentMethod = false,
    isPayWithCheckEnabled = false,
}: Props) {
    const [guestPaymentMethod, setGuestPaymentMethod] =
        useState<PaymentMethod | null>(null)

    const buttonTitle = getButtonTitle(guestPaymentMethod)

    return (
        <>
            <PaymentMethodSelectFormCore
                selectedId={selectedId}
                onChange={onChange}
                paymentMethods={guestPaymentMethod ? [guestPaymentMethod] : []}
                isPayWithCheckEnabled={isPayWithCheckEnabled}
                isExternalPaymentMethod={isExternalPaymentMethod}
            />
            <AddPaymentMethodButton
                title={buttonTitle}
                shouldSkipSetupIntentIfNoUserId={false}
                onPaymentMethodAdded={(
                    stripePaymentMethod: StripePaymentMethod
                ) =>
                    onPaymentMethodAdded({
                        stripePaymentMethod,
                        onChange,
                        setGuestPaymentMethod,
                    })
                }
            />
        </>
    )
}

function onPaymentMethodAdded({
    stripePaymentMethod,
    onChange,
    setGuestPaymentMethod,
}: {
    stripePaymentMethod: StripePaymentMethod
    onChange: (update: Partial<PaymentMethodState>) => void
    setGuestPaymentMethod: Dispatch<SetStateAction<PaymentMethod | null>>
}) {
    const paymentMethod =
        mapStripePaymentMethodToPaymentMethod(stripePaymentMethod)

    setGuestPaymentMethod(paymentMethod)
    onChange({
        paymentMethodId: stripePaymentMethod.id,
        isAchPaymentMethod: isPaymentMethodAch(stripePaymentMethod),
        isExternalPaymentMethod: false,
    })
}

function getButtonTitle(guestPaymentMethod: PaymentMethod | null) {
    return guestPaymentMethod
        ? COPY.CHANGE_PAYMENT_METHOD
        : COPY.ADD_PAYMENT_METHOD
}
